import { useCallback } from 'react';
import DataTable from 'v2/components/DataTable';
import EmptyState from 'v2/components/EmptyState';
import LoadingBar from 'v2/components/LoadingBar';
import OutlinedButton from 'v2/components/OutlinedButton';
import RowActionsMenu from 'v2/components/RowActionsMenu';
import SelectMenu from 'v2/components/SelectMenu';
import CertificationStatesCell from 'v2/layouts/OrganizationWorkspaceLayout/components/UsersTableSection/components/CertificationStatesCell';
import useUsersTable from 'v2/layouts/OrganizationWorkspaceLayout/components/UsersTableSection/hooks/useUsersTable';

import useFeatureFlags from 'hooks/useFeatureFlags';
import useNavigation from 'hooks/useNavigation';
import usePageRoutes from 'hooks/usePageRoutes';
import useRoles from 'hooks/useRoles';

import useTranslation from './hooks/useTranslation';

import {
    ACTIONS_COLUMN_KEY,
    ACTIONS_COLUMN_WIDTH,
    ALL_CERTIFICATION_STATUSES_FILTER,
    CERTIFICATIONS_COLUMN_KEY,
    CERTIFICATION_FILTER_TEST_ID,
    EMAIL_COLUMN_KEY,
    EMPTY_CELL_TEXT,
    EXPIRED_CERTIFICATIONS_FILTER,
    EXPIRING_CERTIFICATIONS_FILTER,
    NAME_COLUMN_KEY,
    NO_CERTIFICATIONS_FILTER,
    ROLE_COLUMN_KEY,
    STATUS_COLUMN_KEY,
    TEST_ID,
    getMemberName,
    getMemberRole
} from './constants';
import type {
    DataTableColumn,
    DataTableRow,
    FunctionComponent,
    Props,
    SelectMenuOption
} from './types';

import styles from './styles.module.scss';

const UsersTableSection: FunctionComponent<Props> = ({ className = '', testId = TEST_ID }) => {
    const { navigate } = useNavigation();
    const { organizationNewUserApplicatorCertification, organizationUser } = usePageRoutes();

    const {
        certificationStatusFilter,
        handleCertificationStatusFilterChange,
        handleLoadMore,
        hasMembers,
        hasNextPage,
        isFetchingMore,
        isLoading,
        members
    } = useUsersTable();

    const { canManageUsers } = useRoles();

    const { STATE_REPORTING } = useFeatureFlags();

    const {
        ACTIONS_LABEL,
        ACTIVE,
        ALL_CERTIFICATION_STATUSES,
        CERTIFICATION_STATUS_FILTER_LABEL,
        CERTIFICATIONS,
        EMAIL,
        EMPTY_DESCRIPTION,
        EMPTY_HEADING,
        EXPIRED_CERTIFICATIONS,
        EXPIRING_CERTIFICATIONS,
        INACTIVE,
        LOAD_MORE,
        MANAGE,
        NAME,
        NO_CERTIFICATIONS,
        NO_MATCHES_DESCRIPTION,
        NO_MATCHES_HEADING,
        ROLE,
        STATUS,
        TABLE_LABEL
    } = useTranslation();

    // The member opens in the workspace edit panel, so the list stays on screen
    // behind it and the path names the member the panel shows.
    const handleOpenMember = useCallback(
        (memberId: string) => navigate(organizationUser(memberId)),
        [navigate, organizationUser]
    );

    const handleAddCertification = useCallback(
        (memberId: string) => navigate(organizationNewUserApplicatorCertification(memberId)),
        [navigate, organizationNewUserApplicatorCertification]
    );

    const handleRowClick = useCallback(
        (_row: DataTableRow, rowIndex: number) => handleOpenMember(members[rowIndex].id),
        [handleOpenMember, members]
    );

    const certificationStatusOptions: SelectMenuOption[] = [
        { label: ALL_CERTIFICATION_STATUSES, value: ALL_CERTIFICATION_STATUSES_FILTER },
        { label: EXPIRING_CERTIFICATIONS, value: EXPIRING_CERTIFICATIONS_FILTER },
        { label: EXPIRED_CERTIFICATIONS, value: EXPIRED_CERTIFICATIONS_FILTER },
        { label: NO_CERTIFICATIONS, value: NO_CERTIFICATIONS_FILTER }
    ];

    const columns: DataTableColumn[] = [
        { key: NAME_COLUMN_KEY, label: NAME },
        { key: EMAIL_COLUMN_KEY, label: EMAIL },
        { key: ROLE_COLUMN_KEY, label: ROLE },
        ...(STATE_REPORTING ? [{ key: CERTIFICATIONS_COLUMN_KEY, label: CERTIFICATIONS }] : []),
        { key: STATUS_COLUMN_KEY, label: STATUS },
        { key: ACTIONS_COLUMN_KEY, label: '', width: ACTIONS_COLUMN_WIDTH }
    ];

    const rows: DataTableRow[] = members.map(member => {
        const memberName = getMemberName(member.firstName, member.lastName);

        return {
            [ACTIONS_COLUMN_KEY]: canManageUsers ? (
                <RowActionsMenu
                    actions={[{ label: MANAGE, onClick: () => handleOpenMember(member.id) }]}
                    ariaLabel={ACTIONS_LABEL}
                />
            ) : null,
            ...(STATE_REPORTING
                ? {
                      [CERTIFICATIONS_COLUMN_KEY]: (
                          <CertificationStatesCell
                              certifications={member.applicatorCertifications}
                              memberName={memberName || member.email}
                              onAddCertification={() => handleAddCertification(member.id)}
                          />
                      )
                  }
                : {}),
            [EMAIL_COLUMN_KEY]: member.email || EMPTY_CELL_TEXT,
            [NAME_COLUMN_KEY]: (
                <span className={styles.memberName}>{memberName || EMPTY_CELL_TEXT}</span>
            ),
            [ROLE_COLUMN_KEY]: getMemberRole(member.roles) || EMPTY_CELL_TEXT,
            [STATUS_COLUMN_KEY]: member.active ? ACTIVE : INACTIVE
        };
    });

    if (isLoading) {
        return <LoadingBar className={className} testId={testId} />;
    }

    if (!hasMembers) {
        return (
            <EmptyState
                className={className}
                description={EMPTY_DESCRIPTION}
                heading={EMPTY_HEADING}
                testId={testId}
            />
        );
    }

    return (
        <div className={`${className} ${styles.usersTableSection}`} data-testid={testId}>
            {STATE_REPORTING ? (
                <SelectMenu
                    className={styles.certificationFilter}
                    label={CERTIFICATION_STATUS_FILTER_LABEL}
                    onChange={handleCertificationStatusFilterChange}
                    options={certificationStatusOptions}
                    testId={CERTIFICATION_FILTER_TEST_ID}
                    value={certificationStatusFilter}
                />
            ) : null}

            {members.length === 0 ? (
                <EmptyState
                    compact
                    description={NO_MATCHES_DESCRIPTION}
                    heading={NO_MATCHES_HEADING}
                />
            ) : (
                <div className={styles.tableScroll}>
                    <DataTable
                        ariaLabel={TABLE_LABEL}
                        columns={columns}
                        onRowClick={handleRowClick}
                        rows={rows}
                    />
                </div>
            )}

            {hasNextPage ? (
                <OutlinedButton
                    className={styles.loadMore}
                    disabled={isFetchingMore}
                    onClick={handleLoadMore}
                >
                    {LOAD_MORE}
                </OutlinedButton>
            ) : null}
        </div>
    );
};

export default UsersTableSection;
